import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { useNetwork } from '@/contexts/NetworkContext'
import { AdUnitsApi, type AdUnit } from '@/services/adUnits/adUnitsApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'
import { SitesApi } from '@/services/sites/sitesApi'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { PickerColumn, type PickerColumnRow } from './PickerColumn'

/** Only active inventory is offered. */
const ACTIVE = 'ACTIVE' as const

/** The most rows the API's list endpoints return in one page. */
const API_PAGE_SIZE = 100

const dimensions = (adUnit: AdUnit) =>
  adUnit.dimensions.map(([width, height]) => `${width}×${height}`).join(', ')

export interface AdUnitBrowserProps {
  value: AdUnit[]
  onChange: (value: AdUnit[]) => void
}

/**
 * Picks ad units by walking the inventory the way the targeting inventory
 * browser does — publisher, site, ad unit — except that only the ad units can
 * be picked. Opening a publisher narrows the sites and ad units to its own;
 * opening a site narrows the ad units to that site's. The ad unit column's
 * search asks the API, so units past the first page can be found.
 */
export function AdUnitBrowser({ value, onChange }: AdUnitBrowserProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const [publisherId, setPublisherId] = useState<string>()
  const [siteId, setSiteId] = useState<string>()
  const [search, setSearch] = useState('')
  const term = useDebouncedValue(search.trim())

  const { data: publishers = [], isLoading: publishersLoading } = useQuery({
    queryKey: ['diagnostics-publishers', scopeKey],
    queryFn: () => EarningsAccountsApi.getAll(undefined, ACTIVE),
  })

  const { data: sites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ['diagnostics-sites', scopeKey, publisherId ?? ''],
    queryFn: () =>
      publisherId
        ? SitesApi.getByEarningsAccount(publisherId, undefined, ACTIVE)
        : SitesApi.getAll(undefined, undefined, ACTIVE),
  })

  const { data: adUnits = [], isLoading: adUnitsLoading } = useQuery({
    queryKey: ['diagnostics-adunits', scopeKey, siteId ?? '', publisherId ?? '', term],
    queryFn: () => {
      const search = term || undefined
      if (siteId) return AdUnitsApi.getBySite(siteId, search, ACTIVE)
      if (publisherId) return AdUnitsApi.getByEarningsAccount(publisherId, search, ACTIVE)
      return AdUnitsApi.getAll(search, ACTIVE)
    },
  })

  const publisherRows: PickerColumnRow[] = useMemo(
    () =>
      publishers.map((publisher) => ({
        id: publisher.id,
        name: publisher.name,
        meta: publisher.countries.join(', '),
      })),
    [publishers]
  )

  const siteRows: PickerColumnRow[] = useMemo(
    () =>
      sites.map((site) => ({
        id: site.id,
        name: site.name,
        sub: site.earningsAccountName,
        meta: site.country,
      })),
    [sites]
  )

  // Picks outside what is loaded stay listed first, so they can be unticked.
  const adUnitRows: PickerColumnRow[] = useMemo(() => {
    const loaded = new Set(adUnits.map((adUnit) => adUnit.id))
    return [...value.filter((adUnit) => !loaded.has(adUnit.id)), ...adUnits].map((adUnit) => ({
      id: adUnit.id,
      name: adUnit.name,
      sub: [adUnit.site?.name, dimensions(adUnit)].filter(Boolean).join(' · '),
    }))
  }, [adUnits, value])

  const toggleAdUnit = (id: string, selected: boolean) => {
    if (!selected) return onChange(value.filter((adUnit) => adUnit.id !== id))
    const adUnit = adUnits.find((candidate) => candidate.id === id)
    if (adUnit) onChange([...value, adUnit])
  }

  const what = (key: string) => t(`targeting.types.${key}.plural`).toLowerCase()

  return (
    <div className="flex flex-col gap-4">
      {/* Fixed column widths, as in the targeting inventory browser; narrow screens scroll. */}
      <div className="flex h-96 overflow-x-auto rounded-md border">
        <div className="flex h-full w-70 shrink-0 grow basis-70 flex-col border-r">
          <PickerColumn
            label={t('targeting.types.publisher.plural')}
            rows={publisherRows}
            selectable={false}
            selectedIds={[]}
            onToggle={() => {}}
            onOpen={(id) => {
              setPublisherId(id === publisherId ? undefined : id)
              setSiteId(undefined)
            }}
            openId={publisherId}
            searchPlaceholder={t('targeting.searchType', { what: what('publisher') })}
            loading={publishersLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        </div>
        <div className="flex h-full w-70 shrink-0 grow basis-70 flex-col border-r">
          <PickerColumn
            label={t('targeting.types.site.plural')}
            rows={siteRows}
            selectable={false}
            selectedIds={[]}
            onToggle={() => {}}
            onOpen={(id) => setSiteId(id === siteId ? undefined : id)}
            openId={siteId}
            searchPlaceholder={t('targeting.searchType', { what: what('site') })}
            loading={sitesLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        </div>
        <div className="flex h-full w-96 shrink-0 grow basis-96 flex-col">
          <PickerColumn
            label={t('targeting.types.adUnit.plural')}
            rows={adUnitRows}
            what={what('adUnit')}
            complete={adUnits.length < API_PAGE_SIZE}
            selectedIds={value.map((adUnit) => adUnit.id)}
            showSelectedToggle
            onToggle={toggleAdUnit}
            onSearch={setSearch}
            searchPlaceholder={t('targeting.searchType', { what: what('adUnit') })}
            loading={adUnitsLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-(--ad-primary-subtle) p-3">
        <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          {t('targeting.selected')}
        </span>
        {value.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('diagnostics.noAdUnits')}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {value.map((adUnit) => (
            <Badge key={adUnit.id} variant="success" className="gap-1.5">
              {adUnit.name}
              <button
                type="button"
                onClick={() => onChange(value.filter((picked) => picked.id !== adUnit.id))}
                aria-label={t('common.remove')}
                className="opacity-60 hover:opacity-100"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  )
}
